import {
  IAvidsColumn,
  IColumn as IRankingReportColumn,
  IHierarchyOption,
} from 'containers/RankingReportCreationContainer/lib/interfaces';
import _, { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import {Container, Spinner} from 'reactstrap';
import {
  DataGrid,
  DataGridSortOrder,
  IColumn,
} from '../../components/ui/DataGrid';
import { getColumnResults } from '../RankingReportCreationContainer/lib/https';
import { getRankingReportExportData } from '../RankingReportCreationContainer/lib/utils';
import {
  getColumnHeader,
  renderHierarchyColumn,
  renderMeasure,
} from './lib/DataGridSubComponents';
import { ExportOptionsComponent } from './lib/exportOptionsComponent';
import {
  exportCSV,
  getColumnNamesList,
  orderAsc,
  sortOnlyValidRowsOnTop,
} from './lib/utils';

interface IProps {
  rankingReportColumns: IRankingReportColumn[];
  requestedAvids: IAvidsColumn[];
  showRollupAverages: boolean;
  showIndustryAverages: boolean;
  listOfAvids: IHierarchyOption[];
  reportName: string;
  shouldRunReport: boolean
  precisionForColumns: any
}

export function RunReportContainer(props: IProps) {
  const {
    rankingReportColumns,
    requestedAvids,
    showRollupAverages,
    showIndustryAverages,
    listOfAvids,
    reportName,
    shouldRunReport,
    precisionForColumns
  } = props;
  const firstColumns: Array<IColumn<any>> = [
    {
      header: 'Hierarchy Record',
      property: 'hierarchyRecord',
      sortable: true,
      render: (record) => renderHierarchyColumn(record, 'hierarchyRecord'),
    },
    {
      header: 'Hierarchy Level',
      property: 'hierarchyLevel',
      sortable: true,
      render: (record) => renderHierarchyColumn(record, 'hierarchyLevel'),
    },
  ];
  const [columns, setColumns] = useState<Array<IColumn<any>>>([]);
  const [propertiesToBeRendered, setPropertiesToBeRendered] = useState([]);
  const [records, setRecords] = useState([]);
  const [industryAverageData, setIndustryAverageData] = useState([]);
  const [industryAverageRecords, setIndustryAverageRecords] = useState([]);
  const [dataGridRecords, setDataGridRecords] = useState([]);
  const [rollupRecords, setRollupRecords] = useState([]);
  const [rollUpScoresData, setRollUpScoresData] = useState([]);
  const [recordsReady, setRecordsReady] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [sortBy, setSortBy] = useState<keyof any>('hierarchyRecord');
  const [sortOrder, setSortOrder] = useState<DataGridSortOrder>('desc');
  const [rankingColumns, setRankingColumns] = useState([]);
  const [rankingColumnsReady, setRankingColumnsReady] = useState(false);
  const [nColumns, setNColumns] = useState([]);
  const [nColumnsReady, setNColumnsReady] = useState(false);

  useEffect(() => {
    if (rankingReportColumns) {
      const sortedRankingReportColumns = rankingReportColumns.sort((a, b) => {
        return orderAsc(a, b);
      });
      const newComingColumns: Array<IColumn<any>> =
        sortedRankingReportColumns.map((rrc) => {
          return {
            header: getColumnHeader(rrc),
            property: `measure-${rrc.id}`,
            render: (record) => renderMeasure(record, rrc),
            align: 'center',
            sortable: true,
          };
        });

      setColumns([...firstColumns, ...newComingColumns]);
    }
  }, [rankingReportColumns]);

  useEffect(() => {
    setPropertiesToBeRendered(
      columns.reduce((acc, curr) => {
        return [...acc, curr.property];
      }, [])
    );
  }, [columns]);

  useEffect(() => {
    const updatedRecords = _.cloneDeep(records);
    let updatedRollUpScores = _.cloneDeep(rollUpScoresData);
    let updatedIndustryAverageData = _.cloneDeep(industryAverageData);
    const numberOfPropertiesToBeFetched = propertiesToBeRendered.filter(
      (property) =>
        property !== 'hierarchyRecord' && property !== 'hierarchyLevel'
    ).length;
    let numberOfPropertiesFetched = 0;
    propertiesToBeRendered.forEach((property) => {
      if (property !== 'hierarchyRecord' && property !== 'hierarchyLevel') {
        getColumnResults(
          JSON.stringify(
            rankingReportColumns.find(
              (c) => c.id === Number(property.slice('measure-'.length))
            )
          ),
          JSON.stringify(
            requestedAvids.reduce((acc, curr) => {
              return acc.concat(curr.id);
            }, [])
          ).slice(1, -1)
        ).then((res) => {
          numberOfPropertiesFetched += 1;
          // add measurement for each requested avid
          requestedAvids.forEach((avid) => {
            const row = updatedRecords.find((r) => r.id === avid.id);
            if (row) {
              const record = res.records.find((r) => r.id === avid.id);
              if (record) {
                row[property] = record.measure;
                row[`n-${res.id}`] = record.n;
                row[`rank-${res.id}`] = record.rank;
              } else {
                row[property] = null;
                row[`n-${res.id}`] = null;
                row[`rank-${res.id}`] = null;
              }
            } else {
              row[property] = null;
              row[`n-${res.id}`] = null;
              row[`rank-${res.id}`] = null;
            }
          });
            if (showIndustryAverages) {
              const newRecord = {
                id: 1652892348345,
                hierarchyRecord: 'Industry Average',
                hierarchyLevel: '-',
              };
              Object.defineProperty(newRecord, `measure-${res.id}`, {
                value: res.industryAverageScore.measure,
                writable: true,
                enumerable: true,
                configurable: true,
              });
              Object.defineProperty(newRecord, `rank-${res.id}`, {
                value: 0,
                writable: true,
                enumerable: true,
                configurable: true,
              });
              Object.defineProperty(newRecord, `n-${res.id}`, {
                value: 0,
                writable: true,
                enumerable: true,
                configurable: true,
              });
              updatedIndustryAverageData = [
                ...updatedIndustryAverageData,
                newRecord
              ];
              if (showRollupAverages) {
                updatedRollUpScores = [
                  ...updatedRollUpScores,
                  ...res.rollupScores.map((r) => {
                    let hierarchyLevelLabel = listOfAvids.find((i) => i.avid.id === r.id)
                        .avid.parentLabel;
                    if (!hierarchyLevelLabel || hierarchyLevelLabel.length === 0) {
                      hierarchyLevelLabel = listOfAvids.find((i) => i.avid.id === r.id)
                          .avid.hierarchyLevel
                    }
                    const newRecord = {
                      id: r.id,
                      hierarchyRecord: listOfAvids.find((i) => i.avid.id === r.id).avid.label,
                      hierarchyLevel: hierarchyLevelLabel && hierarchyLevelLabel.length ? hierarchyLevelLabel : '-',
                    };
                    Object.defineProperty(newRecord, `measure-${res.id}`, {
                      value: Number(r.measure),
                      writable: true,
                      enumerable: true,
                      configurable: true,
                    });
                    Object.defineProperty(newRecord, `rank-${res.id}`, {
                      value: Number(r.rank),
                      writable: true,
                      enumerable: true,
                      configurable: true,
                    });
                    Object.defineProperty(newRecord, `n-${res.id}`, {
                      value: Number(r.n),
                      writable: true,
                      enumerable: true,
                      configurable: true,
                    });
                    return newRecord;
                  }),
                ];
              }
            }
          if (numberOfPropertiesFetched === numberOfPropertiesToBeFetched) {
            setIndustryAverageData(updatedIndustryAverageData);
            setRollUpScoresData(updatedRollUpScores);
            setRecords(updatedRecords);
            setRecordsReady(true);
          }
        });
      }
    });
  }, [propertiesToBeRendered]);

  useEffect(() => {
    setRecords(
      requestedAvids.map((row) => {
        return {
          id: row.id,
          hierarchyRecord: row.label,
          hierarchyLevel: row.parentLabel,
          parentAvid: row.parentAvid,
          parentAvidId: row.parentAvidId,
        };
      })
    );
  }, [requestedAvids]);

  useEffect(() => {
    const rollUpScoresAvids = _.uniq(
      rollUpScoresData.reduce((acc, curr) => {
        return acc.concat(curr.id);
      }, [])
    );
    const soonToBeNewRecords = rollUpScoresAvids.map((avidId) => {
      const selectedRow = rollUpScoresData.find((i) => i.id === avidId);
      return {
        id: avidId,
        hierarchyRecord: selectedRow.hierarchyRecord,
        hierarchyLevel: selectedRow.hierarchyLevel,
        isRollup: true,
      };
    });
    propertiesToBeRendered.forEach((property) => {
      if (property !== 'hierarchyRecord' && property !== 'hierarchyLevel') {
        rollUpScoresAvids.forEach((avidId) => {
          const row = soonToBeNewRecords.find((r) => r.id === avidId);
          const record = rollUpScoresData
            .filter((r) => {
              return r.hasOwnProperty(property);
            })
            .find((r) => r.id === avidId);
          if (row) {
            row[property] = record[property];
            row[`n-${property.slice('measure-'.length)}`] =
              record[`n-${property.slice('measure-'.length)}`];
            row[`rank-${property.slice('measure-'.length)}`] =
              record[`rank-${property.slice('measure-'.length)}`];
          } else {
            row[property] = null;
            row[`n-${property.slice('measure-'.length)}`] = null;
            row[`rank-${property.slice('measure-'.length)}`] = null;
          }
        });
      }
    });
    setRollupRecords(soonToBeNewRecords);
  }, [rollUpScoresData]);

  useEffect(() => {
    const rollUpScoresAvids = _.uniq(
      industryAverageData.reduce((acc, curr) => {
        return acc.concat(curr.id);
      }, [])
    );
    const soonToBeNewRecords = rollUpScoresAvids.map((avidId) => {
      const selectedRow = industryAverageData.find((i) => i.id === avidId);
      return {
        id: avidId,
        hierarchyRecord: selectedRow.hierarchyRecord,
        hierarchyLevel: selectedRow.hierarchyLevel,
        isIndustryAvg: true,
      };
    });
    propertiesToBeRendered.forEach((property) => {
      if (property !== 'hierarchyRecord' && property !== 'hierarchyLevel') {
        rollUpScoresAvids.forEach((avidId) => {
          const row = soonToBeNewRecords.find((r) => r.id === avidId);
          const record = industryAverageData
            .filter((r) => {
              return r.hasOwnProperty(property);
            })
            .find((r) => r.id === avidId);
          if (row) {
            row[property] = record[property];
            row[`n-${property.slice('measure-'.length)}`] =
              record[`n-${property.slice('measure-'.length)}`];
            row[`rank-${property.slice('measure-'.length)}`] =
              record[`rank-${property.slice('measure-'.length)}`];
          } else {
            row[property] = null;
            row[`n-${property.slice('measure-'.length)}`] = null;
            row[`rank-${property.slice('measure-'.length)}`] = null;
          }
        });
      }
    });
    if (showIndustryAverages) {
      setIndustryAverageRecords(soonToBeNewRecords);
    } else {
      setIndustryAverageRecords([])
    }
  }, [industryAverageData, showIndustryAverages]);

  useEffect(() => {
    let updatedDataGridRecords = [];
    if (recordsReady) {
      if (rollupRecords.length > 0 && showRollupAverages) {
        // before getting the final records array, group them by parent avid
        const groupedByParentAvid = _.groupBy(records, 'parentAvidId');
        let formattedRecords = [];
        rollupRecords.forEach((rr) => {
          formattedRecords = [
            ...formattedRecords,
            rr,
            ...groupedByParentAvid[rr.id],
          ];
        });
        updatedDataGridRecords = formattedRecords
      } else {
        updatedDataGridRecords = records
      }
      const industryRecordIndex = updatedDataGridRecords.findIndex((record) => record.isIndustryAvg === true);
      if (showIndustryAverages && industryAverageRecords.length && industryRecordIndex === -1) {
        const middlePosition = Math.floor(updatedDataGridRecords.length / 2);
        updatedDataGridRecords.splice(middlePosition, 0, industryAverageRecords[0]);
      }
      setDataGridRecords(updatedDataGridRecords)
      setPdfReady(true);
    }
  }, [rollupRecords, recordsReady, showRollupAverages, records, showIndustryAverages, industryAverageRecords]);

  useEffect(() => {
    if (recordsReady) {
      const sortedRecords = cloneDeep(records);
      const sortedRollupRecords = cloneDeep(rollupRecords);
      sortedRecords.sort((a, b) => {
        return sortOnlyValidRowsOnTop(a, b, sortBy, sortOrder);
      });
      sortedRollupRecords.sort((a, b) => {
        return sortOnlyValidRowsOnTop(a, b, sortBy, sortOrder);
      });
      setRecords(sortedRecords);
      setRollupRecords(sortedRollupRecords);
    }
  }, [sortBy, sortOrder, recordsReady]);

  useEffect(() => {
    if (rankingReportColumns) {
      const updatedRankingColumns = rankingReportColumns
        .filter((rrc) => {
          return rrc.addRankingColumn;
        })
        .reduce((acc, curr) => {
          return [...acc, `rank-${curr.id}`];
        }, []);
      const updatedNColumns = rankingReportColumns
        .filter((rrc) => {
          return rrc.addNumberOfSurveys;
        })
        .reduce((acc, curr) => {
          return [...acc, `n-${curr.id}`];
        }, []);
      setRankingColumns(updatedRankingColumns);
      setRankingColumnsReady(true);
      setNColumns(updatedNColumns);
      setNColumnsReady(true);
    }
  }, [rankingReportColumns]);

  return (
    <Container fluid>
      {(dataGridRecords.length === 0 && shouldRunReport) && (
        <section className="p-5" style={{display: 'flex', alignContent: 'center', justifyContent: 'center', alignItems: 'center'}}>
          <Spinner color="dark" />
          <span style={{marginLeft: '1rem', fontSize: '1.5rem'}}>Loading Ranking Report results...</span>
        </section>
      )}
      {dataGridRecords.length > 0 && (
        <main>
          <ExportOptionsComponent
            onClick={() => {
              exportCSV(
                getRankingReportExportData(
                  columns,
                  rankingColumns,
                  nColumns,
                  dataGridRecords,
                  getColumnNamesList(rankingReportColumns),
                    precisionForColumns
                ),
                reportName
              );
            }}
            pdfReady={pdfReady}
            reportName={reportName}
            columns={columns}
            rankColumns={rankingColumns}
            nColumns={nColumns}
            records={dataGridRecords}
            rankingReportColumns={rankingReportColumns}
            precisionColumns={precisionForColumns}
            prop8={({ url, loading }) => {
              if (!loading) {
                return (
                  <a
                    className="btn btn-info right"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Export PDF
                  </a>
                );
              } else {
                return (
                  <a
                    className="btn btn-info disabled right"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Preparing PDF
                  </a>
                );
              }
            }}
          />
          <DataGrid
            onSortChange={(sortByValue, sortOrderValue) => {
              setSortBy(sortByValue);
              setSortOrder(sortOrderValue);
            }}
            sortOrder={sortOrder}
            sortBy={sortBy.toString()}
            hideInputs
            disableStripe
            records={dataGridRecords}
            columns={columns}
            pageSize={dataGridRecords.length}
            customSortMethod
            className={'ranking-report-datagrid'}
          />
        </main>
      )}
    </Container>
  );
}
